import * as React from "react";
import { useCallback, useState, useRef } from "react";

// shadcn Components
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import { Sheet, SheetContent } from "../../../components/ui/sheet";
import { Stack } from "../../../components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../components/ui/tooltip";

// MUI Components (to be migrated)
import {
  DataGridPro,
  GridActionsCellItem,
  GridColDef,
  useGridApiRef,
} from "@mui/x-data-grid-pro";

// Icons
import { Download, PanelRight } from "lucide-react";

// Third-party
import Linkify from "linkify-react";

// Components
import { ColumnsButton, getInitialVisibility } from "../Overview/ColumnsButton";

// Internal
import { cn } from "../../../lib/utils";
import { WorkerFrontendSchema } from "../../../api-client";
import { useToken, useWorkspaceContextSlug } from "../../../utils/hooks";
import { CurrentSingleVmMetricsView } from "../Information/MetricsView";
import { fetchLogs } from "./crud";
import { stateToClassName, stateStyles } from "./stateStyles";

const downloadWorkerLogs = async (
  instanceName: string,
  instanceId: number,
  accountSlug: string,
  token: string,
  handleLogDownloadError: (instanceName: string) => void,
) => {
  const logsResponse = await fetchLogs(instanceId, accountSlug, token);
  if (logsResponse.type === "good") {
    const logText = logsResponse.logs.map((item) => item.message).join("\n");
    const link = document.createElement("a");
    link.href = "data:text/plain," + encodeURIComponent(logText);
    link.download = `${instanceName}.txt`;
    link.click();
  } else {
    handleLogDownloadError(instanceName);
  }
};

type InstanceTableProps = {
  instances: WorkerFrontendSchema[];
  loading: boolean;
  instanceRole: string;
};

export const InstanceTable = ({
  instances,
  loading,
  instanceRole,
}: InstanceTableProps): React.ReactElement => {
  const account = useWorkspaceContextSlug();
  const token = useToken();

  const [logError, setLogError] = useState<string | undefined>(undefined);
  const [errorOpen, setErrorOpen] = useState(false);
  const [vmDetail, setVmDetail] = useState<number | undefined>(undefined);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<
    Record<string, boolean>
  >({
    name: false,
    publicIP: false,
    InstanceID: false,
    zone: false,
  });

  const apiRef = useGridApiRef();
  const columnsButtonRef = useRef<HTMLButtonElement>(null);

  const columnStorageKey = `${instanceRole.toLowerCase()}-columns`;

  const handleLogDownloadError = (instanceName: string) => {
    setLogError(`Could not download logs for ${instanceName}`);
    setErrorOpen(true);
  };

  const downloadLogs = useCallback(
    (instanceName: string | undefined, instanceId: number | undefined) => {
      return instanceId && instanceName
        ? () =>
            downloadWorkerLogs(
              instanceName,
              instanceId,
              account,
              token,
              handleLogDownloadError,
            )
        : () => undefined;
    },
    [account, token],
  );

  const instanceColumns = React.useMemo<GridColDef<WorkerFrontendSchema>[]>(
    () => [
      { field: "name", headerName: "Name", flex: 2 },
      {
        field: "privateIP",
        headerName: "Internal IP",
        valueGetter: (params) => params.row.instance?.privateIpAddress || "",
        flex: 1,
      },
      {
        field: "publicIP",
        headerName: "External IP",
        valueGetter: (params) => params.row.instance?.publicIpAddress || "",
        flex: 1,
      },
      {
        field: "State",
        headerName: "Application",
        renderCell: (params) => {
          return (
            <TooltipProvider key={params.row.id + "daskState"}>
              <Tooltip>
                <TooltipTrigger>
                  <span>{params.row.currentState.state}</span>
                </TooltipTrigger>
                <TooltipContent className="whitespace-pre-wrap">
                  <Linkify as="span">{params.row.currentState.reason}</Linkify>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
        flex: 0.5,
      },
      {
        field: "Instance",
        headerName: "Instance",
        renderCell: (params) => {
          const s = (params.row.instance?.retried || 0) > 1 ? "s" : "";
          const retry = params.row.instance?.retryReason
            ? `. Retried ${params.row.instance?.retried} time${s} - ${params.row.instance?.retryReason}`
            : "";
          const instReason =
            (params.row.instance?.currentState.reason || "") + retry;
          return (
            <TooltipProvider key={params.row.id + "instanceState"}>
              <Tooltip>
                <TooltipTrigger>
                  <span>{params.row.instance?.currentState.state}</span>
                </TooltipTrigger>
                <TooltipContent className="whitespace-pre-wrap">
                  <Linkify as="span">{instReason}</Linkify>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
        flex: 0.5,
      },
      {
        field: "InstanceID",
        headerName: "Instance ID",
        valueGetter: (params) => params.row.instance?.backendId,
        flex: 1,
      },
      {
        field: "Instance Type",
        headerName: "Instance Type",
        valueGetter: (params) => params.row.instance?.instanceTypeId,
        flex: 0.5,
      },
      {
        field: "Spot",
        headerName: "Lifecycle",
        valueGetter: (params) =>
          params.row.instance?.spot ? "Spot" : "On-Demand",
        flex: 0.5,
      },
      {
        field: "zone",
        headerName: "Zone",
        valueGetter: (params) => params.row.instance?.zoneName,
        flex: 0.5,
      },
      {
        field: "actions",
        headerName: "Logs",
        type: "actions",
        getActions: (params) => {
          return params.row.instance
            ? [
                <GridActionsCellItem
                  key={params.row.name}
                  icon={<Download className="text-primary h-4 w-4" />}
                  onClick={downloadLogs(
                    params.row.instance?.name,
                    params.row.instance?.id,
                  )}
                  label="Download Logs"
                />,
              ]
            : [];
        },
        width: 60,
      },
      {
        field: "metrics",
        headerName: "Metrics",
        type: "actions",
        getActions: (params) => {
          return params.row.instance
            ? [
                <GridActionsCellItem
                  key={params.row.name}
                  icon={<PanelRight className="h-4 w-4" />}
                  onClick={() => setVmDetail(params.row.instance?.id)}
                  label="VM Metrics"
                />,
              ]
            : [];
        },
        width: 80,
      },
    ],
    [downloadLogs],
  );

  return (
    <>
      {errorOpen && (
        <Alert variant="destructive" className="fixed bottom-4 right-4 w-auto">
          <AlertDescription>{logError}</AlertDescription>
        </Alert>
      )}
      <Sheet
        open={!!vmDetail}
        onOpenChange={(open) => !open && setVmDetail(undefined)}
      >
        <SheetContent className="bg-background">
          {!!vmDetail && <CurrentSingleVmMetricsView instanceId={vmDetail} />}
        </SheetContent>
      </Sheet>
      <Stack spacing={2}>
        <Stack className="flex-row items-center justify-between">
          <div className="text-lg font-semibold">{instanceRole}</div>
          <Stack className="flex-row items-center gap-0">
            <ColumnsButton
              apiRef={apiRef}
              columns={instanceColumns}
              storageKey={columnStorageKey}
              lockNameColumn={false}
              initialVisibilityModel={getInitialVisibility(
                columnStorageKey,
                columnVisibilityModel,
              )}
              onColumnVisibilityChange={(model) => {
                localStorage.setItem(columnStorageKey, JSON.stringify(model));
                setColumnVisibilityModel(model);
              }}
              ref={columnsButtonRef}
            />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => apiRef.current?.exportDataAsCsv()}
                    className="text-gray-500"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Export</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Stack>
        </Stack>
        <DataGridPro
          apiRef={apiRef}
          className={cn(
            "border-none",
            instances.length >= 25 ? "h-[1000px]" : "h-full",
          )}
          getRowClassName={(params) => {
            return cn(
              "min-h-[40px] max-h-[40px]",
              stateToClassName(params.row.currentState.state),
            );
          }}
          disableRowSelectionOnClick
          autoHeight={instances.length < 25}
          hideFooter={instances.length === 1}
          getRowHeight={() => "auto"}
          loading={loading}
          columns={instanceColumns}
          rows={instances}
          initialState={{
            columns: {
              columnVisibilityModel: getInitialVisibility(
                columnStorageKey,
                columnVisibilityModel,
              ),
            },
          }}
          slotProps={{
            panel: {
              anchorEl: columnsButtonRef.current,
              placement: "bottom-end",
            },
          }}
          onColumnVisibilityModelChange={(model) => {
            localStorage.setItem(columnStorageKey, JSON.stringify(model));
            setColumnVisibilityModel(model);
          }}
          sx={{
            "--DataGrid-overlayHeight": "50px",
            "& .MuiDataGrid-row": { ...stateStyles },
          }}
        />
      </Stack>
    </>
  );
};
